// ================================================================================================
// lodash
import _ from "lodash";
// React components
import * as React from "react";
import DropDownMenu from 'material-ui/DropDownMenu';
import IconButton from 'material-ui/IconButton';
import MenuItem from 'material-ui/MenuItem';
import TextField from 'material-ui/TextField';
import Toggle from 'material-ui/Toggle';
import Checkbox from 'material-ui/Checkbox';
import Popover from 'material-ui/Popover/Popover';

// Icons
import AddGroupIcon from 'material-ui/svg-icons/av/playlist-add';
import AddRuleIcon from 'material-ui/svg-icons/content/add';
import DeleteIcon from 'material-ui/svg-icons/action/delete';
import InfoIcon from 'material-ui/svg-icons/action/help-outline';
import PlayIcon from 'material-ui/svg-icons/av/play-circle-outline';
import ClearAllIcon from 'material-ui/svg-icons/communication/clear-all';

// library for color selector
import { SketchPicker } from 'react-color';

// library for single day picker
import DatePicker from 'rsuite/lib/DatePicker';

// library for date range picker
import moment from 'moment';

// Internal Libs
import { Infotip, InfotipProps } from './Infotip';
import { getBool, getRandomSmallKey, isDefined, isFunction, IsNullOrEmpty, IsNullOrWhiteSpace } from '@Utils/Micro';
import { isAnimationEnabled } from '@Utils/Globals';
import { Select, SelectOption, SelectOptions } from './Select';
import { DateRangePicker } from './ReactBootstrapDateRangePicker';
import { DateFormat, Formatters } from '@Utils/Formats';
import { Localization } from './Localization/Localization';
import { DebugEx } from '@Utils/DebugEx';
import { GetQueryBuilderLocalizationDictionary, QueryBuilderLocalizationTextMapKey, QueryBuilderUILabelKey } from './QueryBuilderLocalization';
// ================================================================================================
interface QueryBuilderProps {
    width?: number;
    height?: number;

    QueryStructure: QueryStructure[];
    Fields: Field[];
    DefaultOperators: { [key: string]: Operator[] };
    Combinators: Combinator[];
    TimePickerEnable: boolean;
    onQueryStructureChange?: (struct: QueryStructure[]) => void;
    updateOutputPort?: (text: string, json: QueryStructure[]) => void;

    HideToolbar?: boolean;

    /**
     * Optional key for applying localization.
     */
    localizationKey?: string;
}
// ------------------------------------------------------------------------------------------------
// todo: remove cloned fields
export interface QueryBuilderState {
    QueryStructure: QueryStructure[];
    tooltip: InfotipProps;
    displayColorPicker: string;
}
// ---------------------------------------------------------------------------------------------------
export interface QueryStructure {
    id: string;
    rules: QueryStructure[];
    type: QueryItemType;
    notEnabled?: boolean;
    combinator?: string;
    /**
     * Equals to Field.key
     */
    field?: string;
    value?: any;
    operator?: string;
    readonlyStructure?: boolean;
}
// ---------------------------------------------------------------------------------------------------
export interface Operator {
    key: string;
    label: string;
}
// ---------------------------------------------------------------------------------------------------
export interface Combinator {
    key: string;
    label: string;
}
// ---------------------------------------------------------------------------------------------------
export interface Field {
    key: string;
    label: string;
    type: FieldsItemType;
    useDefaultOperator: boolean;
    operators: Operator[];
    values?: { key: string, label: string }[];
    // minValue?: number;
    // maxValue?: number;
}
// ---------------------------------------------------------------------------------------------------
export enum QueryItemType {
    Group = 0,
    Rule = 1
}
// ---------------------------------------------------------------------------------------------------
export enum FieldsItemType {
    Text = 0,
    Number = 1,
    Date = 2,
    Boolean = 3,
    Color = 4,
    Select = 5
}
// ---------------------------------------------------------------------------------------------------
export class QueryBuilder extends React.Component<QueryBuilderProps, QueryBuilderState> {
    // ---------------------------------------------------------------------------------------------------
    public static defaultProps: QueryBuilderProps = {
        QueryStructure: [],
        Fields: [],
        DefaultOperators: {},
        Combinators: [],
        TimePickerEnable: false,
        onQueryStructureChange: null,
        updateOutputPort: null,
        HideToolbar: null,
        localizationKey: '',
        width: null,
        height: null
    }
    // ---------------------------------------------------------------------------------------------------
    private static _now: moment.Moment;
    private _fieldsCache: { [name: string]: Field } = {};
    private _anchorEl: { [id: string]: any } = {};
    private _query: string;
    // ---------------------------------------------------------------------------------------------------
    constructor(props: QueryBuilderProps) {
        super(props);

        if (!isDefined(QueryBuilder._now))
            QueryBuilder._now = moment().locale('en-gb').utcOffset(0);

        _.forEach(
            this.props.Fields,
            element => this._fieldsCache[element.key] = element
        );
        this.state = {
            displayColorPicker: null,
            tooltip: {
                content: null,
                open: false,
                xPos: 0,
                yPos: 0
            },
            ...this._getStateFromProps(props),
        };
    }
    // ---------------------------------------------------------------------------------------------------
    public render() {
        const {
            width,
            height
        } = this.props;

        const {
            QueryStructure,
            tooltip
        } = this.state;

        return <div
            style={{
                width: width,
                height: height
            }}>
            <Infotip
                key="button-tooltip"
                open={tooltip.open}
                content={tooltip.content}
                xPos={tooltip.xPos}
                yPos={tooltip.yPos}
                maxWidth={350}
            />
            <div
                ref={r => {
                    if (r != null)
                        r.style.paddingRight = r.scrollHeight > r.clientHeight ? '1px' : '0px';
                }}
                style={{
                    width: width,
                    height: height,
                    overflowY: 'auto'
                }}>
                <div style={{ /*border: '1px solid #FFCC80'*/ }}>
                    {this._renderQueryStructure(QueryStructure, [], 1)}
                </div>
            </div>
        </div>;
    }
    // ---------------------------------------------------------------------------------------------------
    public componentDidUpdate(prevProps: QueryBuilderProps) {
        if (!_.isEqual(this.props.QueryStructure, prevProps.QueryStructure) ||
            !_.isEqual(this.props.Fields, prevProps.Fields)) {
            this.setState(this._getStateFromProps(this.props));
        }
    }
    // ---------------------------------------------------------------------------------------------------
    private _getStateFromProps(nextProps: QueryBuilderProps): { QueryStructure: QueryStructure[] } {
        // Update dictionary before setting state as `FieldDictionary` is used for rendering
        this._fieldsCache = {};
        _.forEach(
            nextProps.Fields,
            element => this._fieldsCache[element.key] = element
        );
        return { QueryStructure: _.clone(nextProps.QueryStructure) };
    }
    // ---------------------------------------------------------------------------------------------------
    get rippleProps() {
        const disableRipple = !isAnimationEnabled();
        return {
            disableFocusRipple: disableRipple,
            disableTouchRipple: disableRipple
        };
    }
    // ---------------------------------------------------------------------------------------------------
    private _dropDownMenuCommonProps(
        extraLabelStyle: React.CSSProperties = null,
        extraIconStyle: React.CSSProperties = null
    ): __MaterialUI.Menus.DropDownMenuProps {

        const iconSize = 40;
        return {
            style: {
                height: iconSize
            },
            labelStyle: {
                height: iconSize,
                lineHeight: iconSize + 'px',
                ...extraLabelStyle
            },
            iconStyle: {
                top: 0,
                padding: 0,
                ...extraIconStyle
            },
            maxHeight: 200
        } as __MaterialUI.Menus.DropDownMenuProps;
    }
    // ---------------------------------------------------------------------------------------------------
    private _renderQueryStructure(
        struct: QueryStructure[],
        items: JSX.Element[],
        level: number
    ): JSX.Element[] {

        const indent = (level - 1) * 20;
        const rootStyle: React.CSSProperties = {
            display: 'flex',
            justifyContent: 'space-between',
            //padding: '2px 2px 4px 2px'
            padding: 2
        };

        const firstFlexItemStyle: React.CSSProperties = {
            display: 'flex',
            alignItems: 'center',
        };
        if (level > 1)
            firstFlexItemStyle.width = 'calc(100% - 40px)';

        const locMap = QueryBuilder.LocalizeDictionary(this.props.localizationKey);
        _.forEach(struct, (v, i) => {

            const readonly = getBool(v.readonlyStructure);
            if (v.type == QueryItemType.Group) {

                //const border = level > 1 ? '1px solid #FFCC80' : null;
                const hasRules = _.isArray(v.rules) && v.rules.length > 0;

                items.push(<div style={{
                    ...rootStyle,
                    marginLeft: indent,
                    /*borderLeft: border,
                    borderTop: border,
                    borderBottom: !hasRules ? border : null,
                    marginBottom: !hasRules ? 4 : null*/
                }}>
                    <div style={firstFlexItemStyle}>
                        <Checkbox
                            disabled={readonly}
                            label="NOT"
                            labelPosition="left"
                            style={{
                                width: 'initial',
                                marginTop: 4
                            }}
                            labelStyle={{
                                width: 25,
                                fontWeight: 'initial'
                            }}
                            iconStyle={{
                                marginLeft: 2,
                                marginTop: 1
                            }}
                            defaultChecked={v.notEnabled}
                            onCheck={(event, isInputChecked) => {
                                // Update the negation of element.
                                v.notEnabled = isInputChecked;
                                this.forceUpdate(this._onStructureChange);
                            }} />
                        {/* -------------------------------------------------- */}
                        <DropDownMenu
                            {...this._dropDownMenuCommonProps()}
                            key={'rule-combinator-' + v.id}
                            disabled={readonly}
                            value={v.combinator}
                            onChange={(e, index, menuItemValue) => {
                                // Update the combinator of element.
                                v.combinator = menuItemValue;
                                this.forceUpdate(this._onStructureChange);
                            }}>
                            {this._renderCombinators()}
                        </DropDownMenu>
                        {/* -------------------------------------------------- */}
                        {!readonly
                            ? <IconButton
                                {...this.rippleProps}
                                key={'add-group-' + v.id}
                                onMouseEnter={e => this._showTooltip(
                                    e, locMap[QueryBuilderUILabelKey.Add_Group]
                                )}
                                onMouseLeave={this._hideTooltip}
                                onClick={() => {
                                    const combinators = this.props.Combinators;
                                    if (!_.isArray(combinators) || combinators.length == 0)
                                        return;
                                    // Add group element without rules.
                                    if (!_.isArray(v.rules))
                                        v.rules = [];
                                    v.rules.push({
                                        id: getRandomSmallKey(),
                                        type: QueryItemType.Group,
                                        combinator: combinators[0].key,
                                        rules: []
                                    });
                                    this.forceUpdate(this._onStructureChange);
                                }}>
                                <AddGroupIcon color='#A5D6A7' />
                            </IconButton>
                            : null}
                        {/* -------------------------------------------------- */}
                        {!readonly
                            ? <IconButton
                                {...this.rippleProps}
                                key={'add-rule-' + v.id}
                                onMouseEnter={e => this._showTooltip(
                                    e, locMap[QueryBuilderUILabelKey.Add_Rule]
                                )}
                                onMouseLeave={this._hideTooltip}
                                onClick={() => {
                                    const fields = this.props.Fields;
                                    if (!_.isArray(fields) || fields.length == 0)
                                        return;

                                    const field = fields[0];

                                    // Define the available operators.
                                    let operators: Operator[] = [];
                                    if (field.useDefaultOperator)
                                        operators = this
                                            .props
                                            .DefaultOperators[FieldsItemType[field.type]];
                                    else
                                        operators = field.operators;

                                    if (!_.isArray(operators) || operators.length == 0)
                                        return;

                                    // Add rule element.
                                    if (!_.isArray(v.rules))
                                        v.rules = [];
                                    v.rules.push(
                                        {
                                            id: getRandomSmallKey(),
                                            type: QueryItemType.Rule,
                                            operator: operators[0].key,
                                            field: field.key,
                                            rules: [],
                                            value: null
                                        });
                                    this.forceUpdate(this._onStructureChange);
                                }}>
                                <AddRuleIcon color='#A5D6A7' />
                            </IconButton>
                            : null}
                        {/* -------------------------------------------------- */}
                        {level == 1
                            ? <IconButton
                                {...this.rippleProps}
                                key='clear-all'
                                onMouseEnter={e => this._showTooltip(
                                    e, locMap[QueryBuilderUILabelKey.Clear_All]
                                )}
                                onMouseLeave={this._hideTooltip}
                                onClick={() => this.setState(s => {
                                    // Clear all elements except the root.
                                    s.QueryStructure[0].rules = [];
                                    return s;
                                }, this._onStructureChange)}>
                                <ClearAllIcon color='#EF9A9A' />
                            </IconButton>
                            : null}
                        {/* -------------------------------------------------- */}
                        {level == 1 && this.props.HideToolbar != true
                            ? <React.Fragment>
                                <IconButton
                                    disableFocusRipple
                                    disableTouchRipple
                                    key='query-string'
                                    onMouseEnter={e => {
                                        this._updateQueryText();
                                        this._showTooltip(e, this._query);
                                    }}
                                    onMouseLeave={this._hideTooltip}
                                    style={{ cursor: 'initial' }}>
                                    <InfoIcon color='#90CAF9' />
                                </IconButton>
                                <IconButton
                                    {...this.rippleProps}
                                    key='query-structure-out'
                                    onMouseEnter={e => this._showTooltip(
                                        e, locMap[QueryBuilderUILabelKey.Update_Query]
                                    )}
                                    onMouseLeave={this._hideTooltip}
                                    onClick={this._onSendOutClick}>
                                    <PlayIcon color='#90CAF9' />
                                </IconButton>
                            </React.Fragment>
                            : null}
                    </div>
                    {/* -------------------------------------------------- */}
                    <div>
                        {level > 1
                            ? <IconButton
                                {...this.rippleProps}
                                key={'delete-group-' + v.id}
                                disabled={readonly}
                                style={{ padding: 0 }}
                                onMouseEnter={e => this._showTooltip(
                                    e, locMap[QueryBuilderUILabelKey.Delete]
                                )}
                                onMouseLeave={this._hideTooltip}
                                onClick={() => {
                                    // Delete group element.
                                    struct.splice(i, 1);
                                    this.forceUpdate(this._onStructureChange);
                                }}>
                                <DeleteIcon color='#EF9A9A' />
                            </IconButton>
                            : null}
                    </div>
                </div>);
                if (hasRules)
                    this._renderQueryStructure(v.rules, items, level + 1);
            }
            else if (v.type == QueryItemType.Rule) {

                /*const border = level > 2 ? '1px solid #FFCC80' : null;
                const isLast = i == (struct.length - 1);*/

                items.push(<div style={{
                    ...rootStyle,
                    paddingLeft: 20,
                    marginLeft: indent - 20,
                    /*borderLeft: border,
                    borderBottom: isLast ? border : null,
                    marginBottom: isLast ? 4 : null*/
                }}>
                    <div style={firstFlexItemStyle}>
                        <Select
                            key={'rule-field-' + v.id}
                            isDisabled={readonly}
                            isMulti={false}
                            isClearable={false}
                            containerStyle={{ width: '100%' }}
                            controlStyle={{
                                maxHeight: 200,
                                borderRadius: 2,
                                backgroundColor: 'transparent'
                            }}
                            theme={theme => {
                                return {
                                    ...theme,
                                    borderRadius: 2,
                                    spacing: {
                                        controlHeight: 30,
                                        baseUnit: 2,
                                        menuGutter: 2
                                    }
                                };
                            }}
                            options={_.map(this.props.Fields,
                                field => {
                                    return {
                                        label: field.label,
                                        value: field.key
                                    } as SelectOption;
                                }) as SelectOptions}
                            valueStr={v.field}
                            onChange={value => {
                                v.field = value;
                                const field = this._fieldsCache[value];
                                if (!isDefined(field))
                                    return;

                                if (field.type == FieldsItemType.Color)
                                    v.value = 'rgba(255,255,255,1)';

                                if (_.isArray(field.operators) && field.operators.length > 0)
                                    v.operator = field.operators[0].key;

                                this.forceUpdate(this._onStructureChange);
                            }}>
                        </Select>
                        {/* -------------------------------------------------- */}
                        <DropDownMenu
                            {...this._dropDownMenuCommonProps()}
                            key={'rule-operator-' + v.id}
                            value={v.operator}
                            disabled={readonly}
                            onChange={(e, index, menuItemValue) => {
                                const field = this._fieldsCache[v.field];
                                if (!isDefined(field))
                                    return;

                                v.operator = menuItemValue;
                                if (field.type == FieldsItemType.Date && isDefined(v.value))
                                    v.value = null;

                                this.forceUpdate(this._onStructureChange);
                            }}>
                            {this._renderOperators(v)}
                        </DropDownMenu>
                        {/* -------------------------------------------------- */}
                        {this._renderValueElement('group-' + level + '-element-' + i, v)}
                    </div>
                    {/* -------------------------------------------------- */}
                    <div>
                        <IconButton
                            {...this.rippleProps}
                            key={'delete-rule-' + v.id}
                            style={{ padding: 0 }}
                            onMouseEnter={e => this._showTooltip(
                                e, locMap[QueryBuilderUILabelKey.Delete]
                            )}
                            onMouseLeave={this._hideTooltip}
                            disabled={readonly}
                            onClick={() => {
                                // Delete rule element.
                                struct.splice(i, 1);
                                this.forceUpdate(this._onStructureChange);
                            }}>
                            <DeleteIcon color='#EF9A9A' />
                        </IconButton>
                    </div>
                </div>);
            }
        });
        return items;
    }
    // ---------------------------------------------------------------------------------------------------
    private _renderCombinators(): JSX.Element[] {
        return _.map(this.props.Combinators, element => <MenuItem
            key={'combinator-menu-item-' + element.key}
            value={element.key}
            primaryText={element.label} />);
    }
    // ---------------------------------------------------------------------------------------------------
    private _renderFields(): JSX.Element[] {
        return _.map(this.props.Fields, field => <MenuItem
            key={'field-menu-item-' + field.key}
            value={field.key}
            primaryText={field.label} />);
    }
    // ---------------------------------------------------------------------------------------------------
    private _renderOperators(element: QueryStructure): JSX.Element[] {
        // try get field 
        const field = this._fieldsCache[element.field];
        if (!isDefined(field))
            return;

        if (field.useDefaultOperator)
            return _.map(this.props.DefaultOperators[FieldsItemType[field.type]], operator => <MenuItem
                key={'operator-menu-item-' + operator.key}
                value={operator.key}
                primaryText={operator.label} />);
        else
            return _.map(field.operators, operator => <MenuItem
                key={'operator-menu-item-' + operator.key}
                value={operator.key}
                primaryText={operator.label} />);
    }
    // ---------------------------------------------------------------------------------------------------
    private _renderSelectFieldItems(element: QueryStructure): JSX.Element[] {
        const field = this._fieldsCache[element.field];
        if (!isDefined(field))
            return null;
        return _.map(field.values, field => <MenuItem
            key={'select-menu-item-' + field.key}
            value={field.key}
            primaryText={field.label} />);
    }
    // ---------------------------------------------------------------------------------------------------
    private _isNullOperator(operator: string): boolean {
        return operator.toLowerCase().indexOf('null') != -1;
    }
    // ---------------------------------------------------------------------------------------------------
    private _renderValueElement(elementId: string, element: QueryStructure): JSX.Element {
        // check operator
        if (!isDefined(element.operator))
            return null;

        if (this._isNullOperator(element.operator))
            return null;

        const field = this._fieldsCache[element.field];
        if (!isDefined(field))
            return null;

        const type = field.type;
        switch (type) {
            case FieldsItemType.Text:
            case FieldsItemType.Number:
                return <TextField
                    fullWidth
                    type={type == FieldsItemType.Number ? "number" : "text"}
                    key={'text-field-value-' + element.id}
                    id={'text-field-value-' + element.id}
                    style={{ height: 40 }}
                    underlineStyle={{ bottom: 0 }}
                    underlineFocusStyle={{ bottom: 0 }}
                    defaultValue={isDefined(element.value) ? element.value : ""}
                    onChange={(e, newValue) => {
                        // Update the value of element.
                        element.value = type == FieldsItemType.Number ? parseFloat(newValue) : newValue;
                        this.forceUpdate(this._onStructureChange);
                    }}
                />;
            case FieldsItemType.Boolean:
                return <Toggle
                    defaultToggled={getBool(element.value)}
                    style={{ paddingTop: 10 }}
                    onToggle={(e, isInputChecked) => {
                        // Update the value of element.
                        element.value = isInputChecked;
                        this.forceUpdate(this._onStructureChange);
                    }}
                />
            case FieldsItemType.Select:
                return <DropDownMenu
                    {...this._dropDownMenuCommonProps()}
                    key={'field-select-' + element.id}
                    value={element.value}
                    onChange={(e, index, menuItemValue) => {
                        // Update the value of element.
                        element.value = menuItemValue;
                        this.forceUpdate(this._onStructureChange);
                    }}>
                    {this._renderSelectFieldItems(element)}
                </DropDownMenu>;
            case FieldsItemType.Color:
                return <React.Fragment>
                    <div
                        style={{
                            padding: '5px',
                            background: '#fff',
                            borderRadius: '1px',
                            boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
                            display: 'inline-block',
                            cursor: 'pointer',
                            marginTop: 2
                        }}
                        ref={r => this._anchorEl[elementId] = r}
                        onClick={() => this.setState({ displayColorPicker: elementId })}>
                        <div style={{
                            width: '36px',
                            height: '14px',
                            borderRadius: '2px',
                            background: element.value
                        }} />
                    </div>
                    {this.state.displayColorPicker == elementId
                        ? <Popover
                            open
                            animated
                            style={{ marginTop: 1 }}
                            anchorEl={this._anchorEl[elementId]}
                            anchorOrigin={{ "horizontal": "left", "vertical": "bottom" }}
                            targetOrigin={{ "horizontal": "left", "vertical": "top" }}
                            onRequestClose={() => this.setState({ displayColorPicker: null })}
                            className='popover-hack'>
                            <SketchPicker
                                disableAlpha={false}
                                color={isDefined(element.value) ? element.value : ""}
                                onChange={color => {
                                    // Update the value of element.
                                    element.value = "rgba("
                                        + color.rgb.r + ","
                                        + color.rgb.g + ","
                                        + color.rgb.b + ","
                                        + color.rgb.a + ")";
                                    this.forceUpdate(this._onStructureChange);
                                }} />
                        </Popover>
                        : null}
                </React.Fragment>;

            case FieldsItemType.Date:
                switch (element.operator) {
                    case "=":
                        let date = (!IsNullOrWhiteSpace(element.value)) ? moment(element.value).toDate() : null;
                        return <DatePicker
                            style={{
                                marginTop: 4,
                                borderRadius: 2,
                                backgroundColor: 'transparent'
                            }}
                            format={"YYYY-MM-DD"}
                            value={date}
                            onChange={(data) => {
                                // Update the value of element.
                                element.value = data?.toISOString() ?? null;
                                this.forceUpdate(this._onStructureChange);
                            }}
                            cleanable={false}
                            placement="auto"
                        />;
                    case "range":
                        let startDate;
                        let endDate;
                        if (isDefined(element.value)) {
                            const val = element.value.split(" - ");
                            startDate = new Date(val[0]);
                            endDate = new Date(val[1]);
                        } else
                            startDate = endDate = QueryBuilder._now;

                        const timePicker = this.props.TimePickerEnable;
                        return <DateRangePicker
                            timePicker={timePicker}
                            timePicker24Hour
                            containerStyles={{
                                padding: 0,
                                width: '100%',
                                minWidth: null
                            }}
                            timePickerSeconds
                            startDate={startDate}
                            endDate={endDate}
                            onApply={(event, picker) => {
                                // Update the value of element.
                                element.value = Formatters.datetimeRange(
                                    picker.startDate,
                                    picker.endDate,
                                    DateFormat.ISO,
                                    timePicker
                                );
                                this.forceUpdate(this._onStructureChange);
                            }}
                            localizationKey={this.props.localizationKey}>
                            <TextField
                                fullWidth
                                disabled
                                key={"date-range-text-field"}
                                id={"date-range-text-field"}
                                style={{ height: 40 }}
                                value={isDefined(element.value) ? element.value : ""}
                                inputStyle={{
                                    cursor: 'pointer',
                                    color: 'black'
                                }}
                                underlineDisabledStyle={{
                                    bottom: 0,
                                    borderBottomStyle: 'solid',
                                    borderBottomWidth: '1px'
                                }} />
                        </DateRangePicker>;
                }
        }
        return null;
    }
    // ---------------------------------------------------------------------------------------------------
    private _getQueryText(children: QueryStructure[], combinator: string, text: string): string {

        let hideCombinator: boolean = false;
        _.forEach(children, (v, i) => {

            if (v.type == QueryItemType.Group) {
                if (_.isArray(v.rules) && v.rules.length > 0)
                    if (i == 0) {
                        if (v.notEnabled)
                            text += `not (${this._getQueryText(v.rules, v.combinator, '')}) `;
                        else
                            text += `(${this._getQueryText(v.rules, v.combinator, '')}) `;
                    }
                    else {
                        if (v.notEnabled)
                            text += ` ${combinator} not (${this._getQueryText(v.rules, v.combinator, '')}) `;
                        else
                            text += ` ${combinator} (${this._getQueryText(v.rules, v.combinator, '')}) `;
                    }
            }
            else {
                const field = this._fieldsCache[v.field];
                if (!isDefined(field))
                    return;

                let type: FieldsItemType = field.type;
                if (!isDefined(v))
                    return;

                if (i == 0 || hideCombinator) {
                    if (type == FieldsItemType.Date && v.operator == "range") {
                        if (isDefined(v.value)) {
                            const spliVal = v.value.split(" - ");
                            text += `(${v.field} >= "${spliVal[0]}" and ${v.field} <= "${spliVal[1]}")`;
                            hideCombinator = false;
                        }
                        else {
                            hideCombinator = true;
                        }
                    }
                    else {
                        text += `(${v.field} ${v.operator}`;
                        if (!this._isNullOperator(v.operator)) {
                            text += ` ${this.getQueryValue(v)}`;
                        }
                        text += ") ";
                        hideCombinator = false;
                    }
                } else {
                    if (type == FieldsItemType.Date && v.operator == "range") {
                        if (isDefined(v.value)) {
                            const spliVal = v.value.split(" - ");
                            text += `(${v.field} >= "${spliVal[0]}" and ${v.field} <= "${spliVal[1]}")`;
                        }
                    }
                    else {
                        text += ` ${combinator} (${v.field} ${v.operator}`;
                        if (!this._isNullOperator(v.operator)) {
                            text += ` ${this.getQueryValue(v)}`;
                        }
                        text += ") ";
                    }
                }
            }
            text = _.trimEnd(text);
        });
        return text;
    }
    // ---------------------------------------------------------------------------------------------------
    private getQueryValue(element: QueryStructure): string {
        const field = this._fieldsCache[element.field];
        if (!isDefined(field))
            return 'null';
        switch (field.type) {
            case FieldsItemType.Boolean:
            case FieldsItemType.Number:
                return element.value;
            case FieldsItemType.Color:
            case FieldsItemType.Date:
            case FieldsItemType.Select:
            case FieldsItemType.Text:
            default:
                return `"${element.value}"`;
        }
    }
    // ---------------------------------------------------------------------------------------------------
    private _onSendOutClick = () => {
        this._updateQueryText();

        const { updateOutputPort } = this.props;
        if (isFunction(updateOutputPort))
            updateOutputPort(this._query, this.state.QueryStructure);
    }
    // ---------------------------------------------------------------------------------------------------
    private _updateQueryText = () => {
        const { QueryStructure } = this.state;
        this._query = this._getQueryText(QueryStructure, QueryStructure[0].combinator, "");
    }
    // ---------------------------------------------------------------------------------------------------
    private _onStructureChange = () => {
        // use `onQueryStructureChange` callback
        const { onQueryStructureChange, HideToolbar } = this.props;
        // update widget
        if (isFunction(onQueryStructureChange))
            onQueryStructureChange(this.state.QueryStructure);

        // if no toolbar is shown; send event
        if (HideToolbar)
            this._onSendOutClick();
    }
    // -------------------------------------------------------------------------
    private _showTooltip = (ev: React.MouseEvent<any>, content: any) => this.setState({
        tooltip: {
            open: true,
            content: content,
            xPos: ev.pageX,
            yPos: ev.pageY
        }
    })
    // -------------------------------------------------------------------------
    private _hideTooltip = () => this.setState({ tooltip: { open: false, content: '', xPos: 0, yPos: 0 } })

    // ------------------------------------------------------------------------------------------------
    /**
     * Return label per each button key.
     */
    public static LocalizeDictionary(key: string): any {
        // get default values
        const defaultValues = GetQueryBuilderLocalizationDictionary();

        // check for optional `localizationKey`
        if (!isDefined(key)) {
            // return default value
            return defaultValues;
        }

        // try get localization instance
        const loc = Localization.GetInstance(key);

        if (!isDefined(loc)) {
            DebugEx.TraceError("QueryBuilder: passed `localizationKey`: " + key + " but could not find localization instance!");
            // fallback to defaults
            return defaultValues;
        }

        // instance found; try get text map
        const map = loc.GetTextMap(QueryBuilderLocalizationTextMapKey);
        if (!isDefined(map)) {
            DebugEx.TraceError("QueryBuilder: passed `localizationKey`: " + key + " but could not get text map.");
            // fallback to defaults
            return defaultValues;
        }

        // ok! text map found! let's update response
        const res = defaultValues;
        _.forEach(map, (value, key) => {
            if (!IsNullOrEmpty(value))
                res[key] = value;
        });

        return res;
    }
    // ------------------------------------------------------------------------------------------------ --- Localization
}